import * as React from 'react'
import styled from 'styled-components'

import { styles as S, VideoLoader, LinkAsButton } from '@ulule/owl-kit-components/next'
import { t } from '@owl-nest/localize'
import * as consent from '@owl-nest/consent'
import * as model from '@owl-nest/models'

import * as layout from '../layout'

import * as projectModel from '../../../models/project'
import * as UFE from '../../../../UFE'

const VIDEO_ID = 'welcome-video'

// declare that a globale YT variable exists (will be loaded elswhere if needed)
declare const YT: { Player: new (id: string, config: any) => unknown }

const Coach = styled(layout.Coach)`
  ${VideoLoader} {
    margin: 14px 0;
  }
`

type WelcomeProps = {
  initialOpen: boolean
  project: projectModel.Project
  markStepDone: () => Promise<void>
}

export function Welcome({ project, markStepDone, initialOpen }: WelcomeProps): React.ReactElement<WelcomeProps> | null {
  const manager = project.manager || project.default_manager
  const video = model.project.isDonationBased(project)
    ? model.i18n.get(UFE.VIDEOS.creatorWelcoming.version.donationBased)
    : model.i18n.get(UFE.VIDEOS.creatorWelcoming.version.rewardBased)
  const videoHtml = `<iframe id="${VIDEO_ID}" allow="accelerometer; autoplay; encrypted-media;" src="${video}?enablejsapi=1&showinfo=0&autohide=1&autoplay=1&controls=1&fs=0" frameBorder="0" allowFullScreen />`
  const videoPlaceholder = model.i18n.get(UFE.VIDEOS.creatorWelcoming.placeholder)

  const [open, setIsOpen] = React.useState(initialOpen)
  const playerRef = React.useRef<unknown | null>(null)

  if (manager === undefined) {
    return null
  }

  return (
    <Coach
      closable={true}
      open={open}
      title={t('Welcome to your page')}
      onClose={handleDone}
      avatar={model.image.getUrl(manager.avatar.versions, '128x128')}
    >
      <S.copy.S.regular>
        {t('To kickstart your project, take a look at this video on how to build a successful project page:')}
      </S.copy.S.regular>
      <div onClick={handleVidoClick}>
        <VideoLoader
          consentWall={consent.media.Wall}
          placeholder={videoPlaceholder?.['1x'] ?? ''}
          srcSet={`${videoPlaceholder?.['2x']} 2x, ${videoPlaceholder?.['3x']} 3x`}
          videoHtml={videoHtml}
        />
      </div>
      <LinkAsButton onClick={handleDone} kind="secondary">
        {t('Later')}
      </LinkAsButton>
    </Coach>
  )

  function handleDone(): void {
    setIsOpen(false)
    markStepDone()
  }

  function handleVidoClick(): void {
    if (playerRef.current !== null) {
      return
    }

    setTimeout(() => {
      playerRef.current = new YT.Player(VIDEO_ID, {
        events: {
          onStateChange: onPlayerStateChange,
        },
      })
    })
  }

  function onPlayerStateChange(state: { data: number }): void {
    const hasVideoEnded = state.data === 0
    if (hasVideoEnded) {
      handleDone()
    }
  }
}
